import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Sparkles, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';
import { BACKEND_URL } from './constants';
import { formatTime } from './utils';
import { ImageLightbox } from './ImageLightbox';

export const ChatMessage = ({ message }) => {
  const isSystem = message.sender === 'system';
  const isAdmin = message.sender === 'admin';
  const [imgSrc, setImgSrc] = useState(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [docSrc, setDocSrc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  const adminImgUrl = message.image_path
    ? `${BACKEND_URL}/api/crm/chat-image/${message.image_path}`
    : null;
  const adminVideoUrl = message.video_path
    ? `${BACKEND_URL}/api/crm/chat-video/${message.video_path}`
    : null;

  const loadImage = useCallback(async () => {
    if (imgSrc || loadingImg) return;
    if (adminImgUrl) {
      setImgSrc(adminImgUrl);
      return;
    }
    setLoadingImg(true);
    try {
      const { data } = await api.get(`/crm/messages/${message.id}/image`);
      setImgSrc(`data:${data.mime_type};base64,${data.image_base64}`);
    } catch { /* silent */ }
    finally { setLoadingImg(false); }
  }, [message.id, imgSrc, loadingImg, adminImgUrl]);

  const loadDocument = useCallback(async () => {
    if (docSrc || loadingDoc) return;
    setLoadingDoc(true);
    try {
      const { data } = await api.get(`/crm/messages/${message.id}/document`);
      const mime = data.mime_type || 'application/pdf';
      setDocSrc({ url: `data:${mime};base64,${data.image_base64}`, mime, filename: data.filename || 'documento.pdf' });
    } catch { /* silent */ }
    finally { setLoadingDoc(false); }
  }, [message.id, docSrc, loadingDoc]);

  const loadAudio = useCallback(async () => {
    if (audioSrc || loadingAudio) return;
    if (message.audio_path) {
      setAudioSrc(`${BACKEND_URL}/api/crm/chat-audio/${message.audio_path}`);
      return;
    }
    setLoadingAudio(true);
    try {
      const { data } = await api.get(`/crm/messages/${message.id}/audio`);
      setAudioSrc(`data:${data.mime_type};base64,${data.audio_base64}`);
    } catch { /* silent */ }
    finally { setLoadingAudio(false); }
  }, [message.id, audioSrc, loadingAudio, message.audio_path]);

  const loadVideo = useCallback(async () => {
    if (videoSrc || loadingVideo) return;
    // Outbound admin-sent video → served directly by our /chat-video/{file}
    // endpoint. Inbound (from lead) → fetch via the same base64 API used
    // for image/audio downloads.
    if (adminVideoUrl) {
      setVideoSrc(adminVideoUrl);
      return;
    }
    setLoadingVideo(true);
    try {
      const { data } = await api.get(`/crm/messages/${message.id}/video`);
      setVideoSrc(`data:${data.mime_type};base64,${data.video_base64}`);
    } catch { /* silent — the endpoint may not exist yet for inbound */ }
    finally { setLoadingVideo(false); }
  }, [message.id, videoSrc, loadingVideo, adminVideoUrl]);

  useEffect(() => {
    if (message.message_type === 'image' && (message.media_id || message.image_path)) loadImage();
    if (message.message_type === 'document' && message.media_id) loadDocument();
    if (message.message_type === 'audio' && (message.media_id || message.audio_path)) loadAudio();
    if (message.message_type === 'video' && (message.media_id || message.video_path)) loadVideo();
  }, [message.id]); // eslint-disable-line

  const handleDocDownload = () => {
    if (!docSrc) return;
    const a = document.createElement('a');
    a.href = docSrc.url;
    a.download = docSrc.filename;
    a.click();
  };

  let displayContent = message.content;
  if (typeof displayContent === 'object' && displayContent !== null) displayContent = JSON.stringify(displayContent, null, 2);
  
    if (!displayContent) {
      if (message.message_type === 'audio' || message.type === 'audio') displayContent = '🎙️ [Audio]';
      else if (message.message_type === 'image' || message.type === 'image') displayContent = '📷 [Imagen]';
      else if (message.message_type === 'video' || message.type === 'video') displayContent = '🎥 [Video]';
      else if (message.message_type === 'sticker' || message.type === 'sticker') displayContent = '👾 [Sticker]';
      else if (message.media_url) displayContent = '📎 [Archivo Adjunto]';
      else displayContent = '⚪ [Vacío]';
    }

  return (
    <>
      {lightboxOpen && imgSrc && (
        <ImageLightbox src={imgSrc} onClose={() => setLightboxOpen(false)} />
      )}
      {isSystem ? (
        <div className="flex justify-center my-3" data-testid={`msg-system-${message.id}`}>
          <div className="max-w-[85%] px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-200 text-[11px] shadow-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-400" />
              <div className="whitespace-pre-wrap break-words leading-relaxed">{displayContent}</div>
            </div>
            <p className="text-[9px] text-blue-400/60 mt-1.5 font-medium">{formatTime(message.created_at)}</p>
          </div>
        </div>
      ) : (
      <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-[13px] shadow-sm leading-relaxed ${isAdmin ? 'bg-emerald-600/90 text-white rounded-br-md shadow-emerald-900/5 dark:bg-emerald-700/90' : 'bg-white border border-slate-200/80 text-slate-800 rounded-bl-md shadow-slate-200/50 dark:bg-slate-800/90 dark:border-slate-700/80 dark:text-slate-100'}`}>
          {message.message_type === 'image' ? (
            loadingImg ? <div className="flex items-center gap-2 text-xs opacity-70"><RefreshCw className="w-3 h-3 animate-spin" />Cargando...</div>
            : imgSrc ? (
              <div className="relative group cursor-zoom-in" onClick={() => setLightboxOpen(true)}>
                <img src={imgSrc} alt="img" className="max-w-[220px] rounded-lg" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium bg-black/60 px-2 py-1 rounded-full">
                    🔍 Ver comprobante
                  </span>
                </div>
              </div>
            )
            : <span className="text-xs opacity-60">[Imagen no disponible]</span>
          ) : message.message_type === 'document' ? (
            loadingDoc ? (
              <div className="flex items-center gap-2 text-xs opacity-70"><RefreshCw className="w-3 h-3 animate-spin" />Cargando documento...</div>
            ) : docSrc ? (
              <button
                onClick={handleDocDownload}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-black/20 hover:bg-black/30 transition-colors text-left w-full"
              >
                <span className="text-2xl shrink-0">📄</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold truncate">{docSrc.filename}</p>
                  <p className="text-[10px] opacity-60 mt-0.5">PDF · Toca para descargar</p>
                </div>
                <span className="ml-auto text-lg shrink-0">⬇️</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs opacity-60">
                <span>📄</span> Documento no disponible
              </div>
            )
          ) : message.message_type === 'audio' ? (
            loadingAudio ? (
              <div className="flex items-center gap-2 text-xs opacity-70"><RefreshCw className="w-3 h-3 animate-spin" />Cargando audio...</div>
            ) : audioSrc ? (
              <div className="flex items-center gap-2 min-w-[200px]">
                <span className="text-lg">🎤</span>
                <audio controls className="h-10 max-w-[250px]" preload="metadata">
                  <source src={audioSrc} type={message.mime_type || 'audio/ogg'} />
                  Tu navegador no soporta audio
                </audio>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs opacity-60">
                <span>🎤</span> Audio no disponible
              </div>
            )
          ) : message.message_type === 'video' ? (
            loadingVideo ? (
              <div className="flex items-center gap-2 text-xs opacity-70"><RefreshCw className="w-3 h-3 animate-spin" />Cargando video...</div>
            ) : videoSrc ? (
              <div className="relative">
                <video
                  controls
                  preload="metadata"
                  className="max-w-[260px] rounded-lg"
                  src={videoSrc}
                >
                  Tu navegador no soporta video
                </video>
                {message.caption && (
                  <p className="text-xs mt-1.5 whitespace-pre-wrap break-words opacity-90">{message.caption}</p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs opacity-60">
                <span>🎬</span> Video no disponible
              </div>
            )
          ) : (
            <p className="whitespace-pre-wrap break-words">{displayContent}</p>
          )}
          <p className={`mt-1 text-[10px] leading-none ${isAdmin ? 'text-emerald-100/65 text-right' : 'text-slate-400/80'}`}>
            {message.sender_name && <span>{message.sender_name} · </span>}
            {formatTime(message.created_at)}
            {isAdmin && message.delivery_status === 'failed' && (
              <span
                className="ml-1 inline-flex items-center gap-0.5 font-semibold text-red-300/80"
                title={`No entregado${message.delivery_error_title ? ': ' + message.delivery_error_title : ''}${message.delivery_error_code ? ' (code ' + message.delivery_error_code + ')' : ''}`}
                data-testid={`msg-failed-${message.id}`}
              >
                <AlertTriangle className="w-2.5 h-2.5" /> no entregado
              </span>
            )}
            {isAdmin && message.delivery_status === 'sending' && (
              <span className="ml-1 opacity-50" title="Enviando…">⏳</span>
            )}
            {isAdmin && message.delivery_status === 'sent' && !message.delivery_status_seen && (
              <span className="ml-1 opacity-60" title="Enviado">✓</span>
            )}
            {isAdmin && message.delivery_status === 'read' && <span className="ml-1 text-sky-300/80" title="Leído">✓✓</span>}
            {isAdmin && message.delivery_status === 'delivered' && <span className="ml-1 text-slate-300/80 dark:text-slate-400/80" title="Entregado">✓✓</span>}
          </p>
          {/* Claude OCR extracted receipt data (only on inbound images) */}
          {!isAdmin && message.receipt_data && message.receipt_data.is_receipt && (() => {
            // Defensive: Claude Vision occasionally returns nested objects for
            // sender_name/bank/etc. Rendering an object directly crashes React
            // ("Objects are not valid as a React child") and blanks the whole
            // chat. Coerce every rendered field to a plain string.
            const rd = message.receipt_data || {};
            const asText = (v) => {
              if (v == null) return '';
              if (typeof v === 'string' || typeof v === 'number') return String(v);
              try { return JSON.stringify(v); } catch { return ''; }
            };
            const amountNum = Number(rd.amount);
            const amountValid = rd.amount != null && Number.isFinite(amountNum);
            const senderName = asText(rd.sender_name);
            const bankName = asText(rd.bank);
            const confidencePct = typeof rd.confidence === 'number' && Number.isFinite(rd.confidence)
              ? Math.round(rd.confidence * 100)
              : null;
            // Tampering signals (from the anti-fraud prompt + hash dedup + EXIF)
            const tamperConf = typeof rd.tampering_confidence === 'number'
              ? Math.max(0, Math.min(1, rd.tampering_confidence)) : 0;
            const isEdited = !!rd.is_likely_edited || tamperConf >= 0.5;
            const signals = Array.isArray(rd.tampering_signals)
              ? rd.tampering_signals.map(asText).filter(Boolean) : [];
            // Colour of the whole card shifts to red when tampered.
            const cardClass = isEdited
              ? 'mt-1.5 flex items-start gap-1.5 rounded-md bg-red-950/60 border border-red-500/60 px-2 py-1.5'
              : 'mt-1.5 flex items-start gap-1.5 rounded-md bg-black/25 px-2 py-1.5';
            return (
              <div
                className={cardClass}
                data-testid={`receipt-ocr-${message.id}`}
              >
                <Sparkles className={`w-3 h-3 shrink-0 mt-0.5 ${isEdited ? 'text-red-300' : 'text-amber-300'}`} />
                <div className="text-[10px] leading-tight w-full">
                  <div className={`font-semibold ${isEdited ? 'text-red-200' : 'text-amber-200'}`}>
                    {isEdited ? '⚠️ POSIBLE COMPROBANTE EDITADO' : '🤖 Claude leyó el comprobante'}
                    {confidencePct != null && !isEdited && (
                      <span className="ml-1 opacity-60">{confidencePct}%</span>
                    )}
                    {isEdited && (
                      <span className="ml-1 opacity-80">{Math.round(tamperConf * 100)}% sospecha</span>
                    )}
                  </div>
                  <div className={`space-y-0.5 ${isEdited ? 'text-red-100/90 line-through decoration-red-400/40' : 'text-slate-200/90'}`}>
                    {amountValid && (
                      <div>💰 <strong>${amountNum.toLocaleString('es-AR')}</strong></div>
                    )}
                    {senderName && <div>👤 {senderName}</div>}
                    {bankName && <div>🏦 {bankName}</div>}
                  </div>
                  {/* Recipient CBU cross-check badge — universal anti-fraud
                      that works for ANY bank/wallet without needing bank
                      APIs. Green if the recipient matches one of your
                      configured CBUs, red if it points somewhere else. */}
                  {(() => {
                    const mr = rd.recipient_match_reason;
                    if (mr === "cbu_exact" || mr === "cbu_partial" ||
                        mr === "alias_exact" || mr === "name_fuzzy") {
                      const labelMap = {
                        cbu_exact: 'CBU exacto',
                        cbu_partial: 'CBU parcial (dígitos coinciden)',
                        alias_exact: 'Alias exacto',
                        name_fuzzy: 'Nombre del titular',
                      };
                      const mn = asText(rd.matched_name) || asText(rd.matched_cbu);
                      return (
                        <div
                          className="mt-1 flex items-center gap-1 text-emerald-300 text-[10px] font-semibold"
                          data-testid={`receipt-recipient-match-${message.id}`}
                        >
                          🛡 Va a tu cuenta ({labelMap[mr]}
                          {mn ? ` · ${mn}` : ''})
                        </div>
                      );
                    }
                    if (mr === "no_match" && (rd.recipient_name || rd.recipient_cbu)) {
                      const goingTo = asText(rd.recipient_name) || asText(rd.recipient_cbu);
                      return (
                        <div
                          className="mt-1 flex items-center gap-1 text-red-300 text-[10px] font-semibold"
                          data-testid={`receipt-recipient-nomatch-${message.id}`}
                          title="El destinatario no coincide con ninguno de tus CBUs configurados"
                        >
                          ⚠️ NO va a tu cuenta{goingTo ? ` · destinatario: ${goingTo}` : ''}
                        </div>
                      );
                    }
                    if (mr === "no_config") {
                      return (
                        <div className="mt-1 text-slate-400 text-[10px]">
                          ℹ️ Cargá tus CBUs en Mi Configuración para validar automáticamente
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {isEdited && signals.length > 0 && (
                    <div
                      className="mt-1 pt-1 border-t border-red-500/30 space-y-0.5"
                      data-testid={`receipt-tampering-signals-${message.id}`}
                    >
                      <div className="text-red-300 font-semibold">Señales detectadas:</div>
                      {signals.slice(0, 5).map((s, i) => (
                        <div key={i} className="text-red-200/90 pl-2">• {s}</div>
                      ))}
                      <div className="text-red-300/80 mt-1 font-semibold">
                        🛑 NO cargar hasta verificar manualmente
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      )}
    </>
  );
};
